import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { getEntities, getEntity } from 'app/entities/mail-group/mail-group.reducer';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { createEntity, reset } from 'app/entities/order/order.reducer';
import { Loading } from 'app/shared/loading';
import { toast } from 'react-toastify';
import { SocialList } from 'app/config/constants';
import { colors, formatVND } from 'app/shared/theme/colors';
import { useTranslation } from 'app/shared/i18n';

export const Services = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const mailGroupList = useAppSelector(state => state.mailGroup.entities);
  const isAuthenticated = useAppSelector(state => state.authentication.isAuthenticated);
  const [dataCount, setDataCount] = useState<any>();
  const [loyaltyMap, setLoyaltyMap] = useState<Record<number, { eligible: boolean; loyaltyPrice: number; expiresAt: string }>>({});

  useEffect(() => {
    dispatch(getEntities({ sort: `id,ASC` }));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoyaltyMap({});
      return;
    }
    axios
      .get('/api/mail-types/loyalty-status')
      .then(res => {
        setLoyaltyMap(res.data?.data ?? {});
      })
      .catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    const eventSource = new EventSource('/api/mail/stream/count');
    eventSource.onmessage = event => {
      try {
        const data = JSON.parse(event.data);
        setDataCount(data.data);
      } catch (e) {
        // ignore malformed event payload
      }
    };
    eventSource.onerror = () => {
      eventSource.close();
    };
    return () => eventSource.close();
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const el = document.getElementById(hash.substring(1));
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [mailGroupList]);

  // Tính tổng tài khoản cho mỗi group
  const getGroupTotal = group => {
    if (!dataCount || !group?.mailTypes) return 0;
    return group.mailTypes.reduce((sum, mt) => sum + ((dataCount && dataCount[mt.key]) || 0), 0);
  };

  return (
    <section id="services" style={{ padding: '20px 24px 60px' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{t('services.title')}</h2>
          <span className="ms-badge">Realtime</span>
        </div>
        {mailGroupList.map(group => (
          <TableService group={group} key={group.id} dataCount={dataCount} groupTotal={getGroupTotal(group)} loyaltyMap={loyaltyMap} />
        ))}
      </div>
    </section>
  );
};

const TableService = props => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(state => state.authentication.isAuthenticated);
  const navigate = useNavigate();
  const [typeBuySelected, setTypeBuySelected] = useState(null);
  const social = useMemo(() => SocialList.find(x => x.id === props?.group?.social), [props.group]);

  const onBuy = type => {
    if (!isAuthenticated) navigate('/login');
    else setTypeBuySelected(type);
  };
  const onLoadMore = (id: number) => dispatch(getEntity(id));

  return (
    <div className="ms-card" id={`${props.group.social}_${props.group.id}`} style={{ marginBottom: 20, overflow: 'hidden' }}>
      {/* Group header with icon + total count */}
      <div className="ms-product-group__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="ms-product-group__icon">
            {social?.id && (
              <img
                alt={social.label}
                width={20}
                height={20}
                src={`/content/images/socials/${social.id}.svg`}
                style={{ filter: 'brightness(0) invert(1) opacity(0.8)' }}
              />
            )}
          </div>
          <div className="ms-product-group__name">{props.group.name}</div>
          <span className="ms-badge ms-badge--dim" style={{ fontSize: 11 }}>
            {props.group?.mailTypes?.length || 0} {t('services.products')}
          </span>
        </div>
        <span className="ms-badge ms-badge--dim" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
          {props.groupTotal.toLocaleString()} {t('services.accounts')}
        </span>
      </div>

      {/* Product table */}
      <div style={{ overflowX: 'auto' }}>
        <table className="ms-table" style={{ minWidth: 700, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '36%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '13%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ textAlign: 'center' }}>ID</th>
              <th style={{ textAlign: 'left' }}>{t('services.product')}</th>
              <th style={{ textAlign: 'center' }}>Live</th>
              <th style={{ textAlign: 'center' }}>{t('services.country')}</th>
              <th style={{ textAlign: 'center' }}>{t('services.unitPrice')}</th>
              <th style={{ textAlign: 'center' }}>{t('services.quantity')}</th>
              <th style={{ textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {props.group?.mailTypes?.map(type => {
              const count = (props.dataCount && props.dataCount[type.key]) || 0;
              const qtyClass = count > 800 ? 'ms-qty--high' : count > 100 ? 'ms-qty--mid' : 'ms-qty--low';
              return (
                <tr key={type.id}>
                  <td style={{ textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: colors.dim }}>
                    {type.id}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {social?.id && (
                        <img
                          alt={social.label}
                          width={22}
                          height={22}
                          src={`/content/images/socials/${social.id}.svg`}
                          style={{ filter: 'brightness(0) invert(1) opacity(0.7)', flexShrink: 0 }}
                        />
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>{type.name}</span>
                          {type.isHot && (
                            <span className="ms-badge ms-badge--danger" style={{ fontSize: 10 }}>
                              🔥 HOT
                            </span>
                          )}
                        </div>
                        {type.note && (
                          <div
                            style={{
                              fontSize: 11,
                              color: colors.dim,
                              marginTop: 2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {type.note}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ textAlign: 'center', color: colors.muted, fontSize: 13 }}>{type.ttl}</td>
                  <td style={{ textAlign: 'center' }}>
                    <img src="/content/images/frag_vn.svg" alt="VN" style={{ width: 20, height: 14 }} />
                  </td>
                  <td style={{ textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>
                    {props.loyaltyMap[type.id]?.eligible ? (
                      <div>
                        <span style={{ textDecoration: 'line-through', color: colors.dim, fontSize: 11 }}>
                          {formatVND(type.price ?? 0)}
                        </span>
                        <span style={{ fontWeight: 700, color: '#f59e0b', marginLeft: 6 }}>
                          {formatVND(props.loyaltyMap[type.id].loyaltyPrice)}
                        </span>
                        <div style={{ fontSize: 9, color: '#f59e0b', fontFamily: 'inherit' }}>
                          {props.loyaltyMap[type.id].mode === 'promotion' ? 'Khuyen mai' : 'Gia than thiet'}
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontWeight: 700, color: colors.success }}>{formatVND(type.price ?? 0)}</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`ms-qty ${qtyClass}`}>{count.toLocaleString()}</span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      className="ms-btn ms-btn--primary"
                      style={{ padding: '8px 18px', fontSize: 13, whiteSpace: 'nowrap' }}
                      onClick={() => onBuy(type)}
                    >
                      {t('services.buyNow')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {props.group.hasMore && (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <button className="ms-btn ms-btn--ghost" onClick={() => onLoadMore(props.group.id)}>
            {t('services.loadMore')}
          </button>
        </div>
      )}

      {!!typeBuySelected && (
        <ModalConfirmBuy
          isOpen={!!typeBuySelected}
          type={typeBuySelected}
          handleClose={() => setTypeBuySelected(null)}
          loyaltyMap={props.loyaltyMap}
        />
      )}
    </div>
  );
};

// ============== PURCHASE MODAL ==============
const ModalConfirmBuy = props => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const entity = useAppSelector(state => state.order.entity);
  const loading = useAppSelector(state => state.order.updating);
  const rank = useAppSelector(state => state.authentication.account.rank);
  const [quantity, setQuantity] = useState(1);
  // Chỉ áp discount nếu rank của user còn nằm trong danh sách hạng ĐANG BẬT (server đã lọc /api/ranks).
  // Nếu chưa fetch xong / rank đã tắt -> coi discount 0 (hiện full price = an toàn, khớp giá server thu thật).
  const [enabledRankIds, setEnabledRankIds] = useState<Set<number>>(new Set());
  useEffect(() => {
    axios
      .get('/api/ranks')
      .then(res => setEnabledRankIds(new Set((res.data || []).map((r: any) => r.id))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    dispatch(reset());
    setQuantity(1);
  }, [props.type]);

  const getDiscount = useMemo(() => {
    if (!rank || !props.type || !enabledRankIds.has(rank.id)) return 0;
    const product = rank?.product?.trim();
    if (!product) return rank.discountPercent;
    return product
      .split(',')
      .map(s => s.trim())
      .includes(props.type.id + '')
      ? rank.discountPercent
      : 0;
  }, [rank, props.type, enabledRankIds]);

  const loyalty = props.loyaltyMap?.[props.type?.id];
  const basePrice = useMemo(() => {
    if (!props.type) return 0;
    return loyalty?.eligible ? loyalty.loyaltyPrice : props.type.price;
  }, [props.type, loyalty]);
  const unitPrice = useMemo(() => (basePrice ? (basePrice * (100 - getDiscount)) / 100 : 0), [basePrice, getDiscount]);
  const total = unitPrice * quantity;

  const handleBuy = () => {
    dispatch(createEntity({ quantity, mailTypeId: +props.type.id }));
  };
  const convertMailPass = (x: string) => {
    const parts = x.split('|');
    return parts[0] + '|' + parts[1];
  };

  // Parse entity.result once per change. Returns [] for invalid/empty data
  // (no toast here — toast is shown by the action handlers when items.length === 0).
  const resultItems = useMemo<string[]>(() => {
    if (!entity?.result) return [];
    try {
      const parsed = JSON.parse(entity.result);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }, [entity?.result]);

  const onCopy = () => {
    if (resultItems.length === 0) {
      toast.error('Không có dữ liệu để copy');
      return;
    }
    navigator.clipboard.writeText(resultItems.map(x => convertMailPass(x)).join('\n'));
    toast.success(t('common.copySuccess'));
  };
  const onCopyFullInfo = () => {
    if (resultItems.length === 0) {
      toast.error('Không có dữ liệu để copy');
      return;
    }
    navigator.clipboard.writeText(resultItems.join('\n'));
    toast.success(t('common.copySuccess'));
  };
  const onDownload = () => {
    if (resultItems.length === 0) {
      toast.error('Không có dữ liệu để tải');
      return;
    }
    const data = resultItems.join('\n');
    const blob = new Blob([data], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${entity.code}.txt`;
    document.body.appendChild(link);
    link.click();
    // Defer cleanup so the browser has time to start the download stream.
    // Revoking the blob URL synchronously can cause downloads to fail on slow connections.
    setTimeout(() => {
      try {
        document.body.removeChild(link);
      } catch (e) {
        /* already removed */
      }
      window.URL.revokeObjectURL(url);
    }, 1000);
  };

  return (
    <Modal isOpen={props.isOpen} toggle={props.handleClose} centered modalTransition={{ timeout: 20 }} backdropTransition={{ timeout: 10 }}>
      {loading && (
        <div style={{ padding: 50, textAlign: 'center' }}>
          <div
            style={{
              width: 40,
              height: 40,
              border: `3px solid ${colors.border}`,
              borderTopColor: colors.accent,
              borderRadius: '50%',
              margin: '0 auto 14px',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <div style={{ fontSize: 14, color: colors.muted }}>{t('services.processing')}</div>
          <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
        </div>
      )}
      {!loading && entity?.id ? (
        <>
          <ModalHeader toggle={props.handleClose}>{t('services.purchaseSuccess')}</ModalHeader>
          <ModalBody>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'rgba(16,185,129,0.15)',
                  border: '2px solid rgba(16,185,129,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  margin: '0 auto 12px',
                }}
              >
                ✓
              </div>
              <div style={{ fontSize: 11, color: colors.dim, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                {t('services.orderCode')}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: colors.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                {entity.code}
              </div>
              <div style={{ fontSize: 13, color: colors.muted, marginTop: 4 }}>
                {t('services.qty')}: {entity.quantity} — {t('services.total')}: {formatVND(entity.totalPrice)}
              </div>
            </div>
            <div
              style={{
                padding: 14,
                borderRadius: 10,
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                maxHeight: 180,
                overflowY: 'auto',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                lineHeight: 1.8,
                color: colors.muted,
              }}
            >
              {resultItems.map((x, i) => (
                <div key={`${entity.id}-${i}`}>{convertMailPass(x)}</div>
              ))}
            </div>
          </ModalBody>
          <ModalFooter style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button className="ms-btn ms-btn--secondary" onClick={onCopy} style={{ padding: '8px 16px', fontSize: 12 }}>
              📋 Copy email|pass
            </button>
            <button className="ms-btn ms-btn--secondary" onClick={onCopyFullInfo} style={{ padding: '8px 16px', fontSize: 12 }}>
              📋 Copy full
            </button>
            <button className="ms-btn ms-btn--primary" onClick={onDownload} style={{ padding: '8px 16px', fontSize: 12 }}>
              ⬇ Download .txt
            </button>
          </ModalFooter>
        </>
      ) : !loading ? (
        <>
          <ModalHeader toggle={props.handleClose}>{t('services.buyProduct')}</ModalHeader>
          <ModalBody>
            {/* Product info */}
            <div
              style={{
                padding: 14,
                borderRadius: 10,
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: colors.accentDim,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  flexShrink: 0,
                }}
              >
                📧
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>{props.type.name}</div>
                {props.type.note && <div style={{ fontSize: 11, color: colors.dim, marginTop: 2 }}>{props.type.note}</div>}
              </div>
            </div>

            {/* Quantity stepper */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: colors.muted, marginBottom: 8 }}>
                {t('services.quantity')}
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  className="ms-btn ms-btn--secondary"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  style={{ width: 40, height: 40, padding: 0, fontSize: 18 }}
                >
                  −
                </button>
                <input
                  className="ms-input"
                  type="number"
                  value={quantity}
                  onChange={e => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  style={{ textAlign: 'center', fontSize: 18, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", height: 40 }}
                />
                <button
                  type="button"
                  className="ms-btn ms-btn--secondary"
                  onClick={() => setQuantity(quantity + 1)}
                  style={{ width: 40, height: 40, padding: 0, fontSize: 18 }}
                >
                  +
                </button>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                {[10, 100, 1000, 2000].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setQuantity(n)}
                    style={{
                      flex: 1,
                      padding: '5px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 500,
                      fontFamily: "'JetBrains Mono', monospace",
                      cursor: 'pointer',
                      background: quantity === n ? colors.accentDim : colors.surface,
                      border: `1px solid ${quantity === n ? colors.accent + '44' : colors.border}`,
                      color: quantity === n ? colors.accent : colors.muted,
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Price summary */}
            <div style={{ padding: 14, borderRadius: 10, background: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: colors.muted }}>{t('services.unitPrice')}</span>
                <span style={{ fontSize: 13, fontFamily: "'JetBrains Mono', monospace" }}>{formatVND(props.type.price)}</span>
              </div>
              {getDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: colors.danger }}>
                    {t('services.discount')} ({getDiscount}%)
                  </span>
                  <span style={{ fontSize: 13, color: colors.danger, fontFamily: "'JetBrains Mono', monospace" }}>
                    -{formatVND(((props.type.price * getDiscount) / 100) * quantity)}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: colors.muted }}>{t('services.quantity')}</span>
                <span style={{ fontSize: 13, fontFamily: "'JetBrains Mono', monospace" }}>×{quantity}</span>
              </div>
              <div style={{ height: 1, background: colors.border, margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{t('services.grandTotal')}</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: colors.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                  {formatVND(total)}
                </span>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <button className="ms-btn ms-btn--ghost" type="button" onClick={props.handleClose}>
              {t('common.cancel')}
            </button>
            <button className="ms-btn ms-btn--primary" type="button" onClick={handleBuy}>
              {t('services.confirmPurchase')}
            </button>
          </ModalFooter>
        </>
      ) : null}
    </Modal>
  );
};
